import {connect} from "react-redux";
import {FloatingDialog} from "../dialogs/FloatingDialog";
import {selectDictionary} from "../../redux/selectors/firebase";
import {Accordion, AccordionDetails, AccordionSummary, alpha, Box, InputBase, styled, Typography} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, {useContext, useEffect, useState} from "react";
import {FirebaseContext} from "../firebase/FirebaseProvider";
import {Add} from "@mui/icons-material";
import {selectUserId} from "../../redux/selectors/editor";

const Input = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
}));

const InputIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 1),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 5),
        // vertical padding + font size from searchIcon
        transition: theme.transitions.create('width')
    },
}));

const useStyles = makeStyles(theme => ({
    container: {
        alignItems: "flex-end",
        display: "flex",
        flexDirection: "row",
        bottom: theme.spacing(4),
        left: theme.spacing(4),
        pointerEvents: "none",
        position: "absolute",
        height: "50%",
        width: "80%"
    }
}));
interface IWord {
    user: string
    date: number
}
interface IProps {
    dictionary: {[word: string]: IWord}
    onClose: () => void
    project: string
    user: string
}
function Dictionary({dictionary, onClose, project, user}: IProps) {
    const classes = useStyles();
    const [addWord, setAddWord] = useState("");
    const [users, setUsers] = useState<{[id: string]: string}>({});
    const {getUser, setDictionaryEntry} = useContext(FirebaseContext);
    const addWordToDictionary = (word: string) => {
        setDictionaryEntry(project, user, word).then(() => setAddWord(""));
    };
    const handleAddWordChange = ({currentTarget}: React.ChangeEvent<HTMLInputElement>) => {
        setAddWord(currentTarget.value);
    };
    const handleAddWordKeyPress = ({key}: React.KeyboardEvent<HTMLImageElement>) => {
        if (key === "Enter") {
            addWordToDictionary(addWord);
        }
    };

    useEffect(() => {
        if (dictionary) {
            const unique = Object.values(dictionary).map(details => details.user)
                .filter((value, index, self) => self.indexOf(value) === index)
                .filter(userId => users[userId] === undefined);

            if (Object.keys(unique).length > 0)
            {
                const update: {[id: string]: string} = {};

                Promise.all(unique.map(userId => getUser(userId).then(details => update[userId] = details.name)))
                    .then(() => {
                        setUsers({...users, ...update});
                    });
            }
        }
    }, [dictionary, getUser, setUsers, users]);
    return (
        <Box className={classes.container}>
            <FloatingDialog title="Dictionary" onClose={onClose} tools={
                <Input>
                    <InputIconWrapper>
                        <Add/>
                    </InputIconWrapper>
                    <StyledInputBase
                        placeholder="Add word…" value={addWord}
                        onChange={handleAddWordChange} onKeyPress={handleAddWordKeyPress}/>
                </Input>
            }>
                <Box m={1}>
                    {Object.keys(dictionary || {}).map(word => (
                        <Accordion key={word}>
                            <AccordionSummary>
                                <Typography>{word}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>{users[dictionary[word].user] || "Unknown"}</Typography>
                                <Typography>{new Date(dictionary[word].date).toLocaleDateString()}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </FloatingDialog>
        </Box>
    );
}
const mapStateToProps = (store: never) => ({
    dictionary: selectDictionary(store),
    user: selectUserId(store)
});

export default connect(mapStateToProps, {
})(Dictionary);
